import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BarChart3, HardHat, Plus } from 'lucide-react';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import WorkersTable from '../components/workers/WorkersTable';
import WorkerForm from '../components/workers/WorkerForm';
import WorkerLoginModal from '../components/workers/WorkerLoginModal';
import { apiGet, apiPost, apiPut, apiDelete } from '../api/client';
import { usePortalAccess } from '../hooks/usePortalAccess';

function Workers() {
  const nav = useNavigate();
  // A supervisor may be given this screen without being given everything on
  // it: looking at who is free is one thing, taking someone on is another.
  const { can, isPortal } = usePortalAccess();
  const mayEdit = can('editWorker');
  const mayAssign = can('assignWork');
  // Removing someone, and handing out a login, stay with the office however
  // the switches are set — the server allows neither to a portal login, and
  // a button that can only fail is worse than no button.
  const mayRemove = !isPortal;
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bulkTarget, setBulkTarget] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // null = closed, {} = add new, worker object = edit
  const [formTarget, setFormTarget] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loginTarget, setLoginTarget] = useState(null);

  // GET /api/workers on mount
  const fetchWorkers = async () => {
    try {
      setLoading(true);
      setError(null);
      // Deleting a worker sets status INACTIVE, so only ACTIVE ones belong here.
      const data = await apiGet('/workers', { status: 'ACTIVE' });
      setWorkers(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, []);

  // POST /api/workers
  const createWorker = (payload) => apiPost('/workers', payload);

  // PUT /api/workers/:id
  const updateWorker = (id, payload) => apiPut(`/workers/${id}`, payload);

  // DELETE /api/workers/:id  (soft delete — sets status INACTIVE)
  const removeWorker = (id) => apiDelete(`/workers/${id}`);

  // The backend Worker model only persists name/phone/role/status today, so
  // map the ERP form fields onto what the API actually requires. The full
  // form payload is still sent alongside so nothing is lost once the API
  // is extended to store the rest of the master fields.
  const toApiPayload = (form) => ({
    ...form,
    name: form.ledgerName,
    phone: form.mobNo || form.phNo || null,
    role: form.role,
    status: form.isActive ? 'ACTIVE' : 'INACTIVE',
  });

  const handleFormSubmit = async (payload) => {
    try {
      setFormLoading(true);
      setFormError(null);
      if (!payload.ledgerName?.trim()) throw new Error('Name (Ledger Name) is required');
      if (!payload.role) throw new Error('Role is required');
      const apiPayload = toApiPayload(payload);
      if (formTarget?.id) {
        await updateWorker(formTarget.id, apiPayload);
      } else {
        await createWorker(apiPayload);
      }
      await fetchWorkers();
      setFormTarget(null);
    } catch (err) {
      setFormError(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      await removeWorker(deleteTarget.id);
      setDeleteTarget(null);
      await fetchWorkers();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      setBulkLoading(true);
      const results = await Promise.allSettled(bulkTarget.map((w) => removeWorker(w.id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) setError(`${bulkTarget.length - failed} removed, ${failed} could not be removed.`);
      setBulkTarget(null);
      await fetchWorkers();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        icon={HardHat}
        title="Workers"
        subtitle="Click a worker to see their jobs, pay and rate card"
        actions={
          <>
            <button className="btn-outline" onClick={() => nav('/performance')}>
              <BarChart3 size={15} /> Performance
            </button>
            {mayEdit && (
              <button className="btn-primary" onClick={() => { setFormError(null); setFormTarget({}); }}>
                <Plus size={15} /> Add Worker
              </button>
            )}
          </>
        }
      />

      <WorkersTable
        workers={workers}
        loading={loading}
        error={error}
        onRowClick={(worker) => nav(`/workers/${worker.id}`)}
        mayEdit={mayEdit}
        mayAssign={mayAssign}
        onEdit={mayEdit ? (worker) => { setFormError(null); setFormTarget(worker); } : undefined}
        onDelete={mayRemove ? (worker) => setDeleteTarget(worker) : undefined}
        onBulkDelete={mayRemove ? (rows) => setBulkTarget(rows) : undefined}
        onLogin={mayRemove ? (worker) => setLoginTarget(worker) : undefined}
      />

      {loginTarget && (
        <WorkerLoginModal worker={loginTarget} onClose={() => setLoginTarget(null)} onChanged={fetchWorkers} />
      )}

      <Modal
        open={!!formTarget}
        onClose={() => setFormTarget(null)}
        title={formTarget?.id ? 'Edit Worker' : 'Add New Worker'}
        size="lg"
      >
        {formTarget && (
          <WorkerForm
            key={formTarget.id || 'new'}
            worker={formTarget.id ? formTarget : null}
            onSubmit={handleFormSubmit}
            onCancel={() => setFormTarget(null)}
            saving={formLoading}
            error={formError}
          />
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Worker"
        message={`Remove "${deleteTarget?.ledgerName || deleteTarget?.name}" from the workers list? Their past orders and saved rates are kept, and an admin can reactivate them later.`}
        busy={deleteLoading}
        onConfirm={handleDeleteConfirm}
      />

      <ConfirmDialog
        open={!!bulkTarget}
        onClose={() => setBulkTarget(null)}
        title={`Remove ${bulkTarget?.length || 0} workers`}
        message={`Remove ${bulkTarget?.length || 0} selected workers from the list? Their past orders and saved rates are kept.`}
        busy={bulkLoading}
        onConfirm={handleBulkDeleteConfirm}
      />
    </div>
  );
}

export default Workers;